import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, ExternalLink, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { transactionsApi, projectsApi } from '../lib/api';
import { formatCurrency, formatDate, TRANSACTION_CATEGORY_GROUPS } from '../lib/utils';
import { toast } from 'sonner';

export const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // all, ytd, last30, last90, custom
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [transactionsRes, projectsRes] = await Promise.all([
        transactionsApi.getAll(),
        projectsApi.getAll()
      ]);
      setTransactions(transactionsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'Expense': return 'bg-red-50 text-red-700 border-red-200';
      case 'Capital_In': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Sale_Revenue': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Reimbursement': case 'Refund': return 'bg-teal-50 text-teal-700 border-teal-200';
      case 'Purchase_Price': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'EMD': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  // Date filtering helper
  const isWithinDateRange = (dateStr) => {
    if (dateFilter === 'all') return true;
    if (!dateStr) return false;
    
    const date = new Date(dateStr);
    const now = new Date();
    
    switch (dateFilter) {
      case 'ytd':
        return date.getFullYear() === now.getFullYear();
      case 'last30':
        const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));
        return date >= thirtyDaysAgo;
      case 'last90':
        const ninetyDaysAgo = new Date(now.setDate(now.getDate() - 90));
        return date >= ninetyDaysAgo;
      case 'custom':
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          return date >= start && date <= end;
        }
        return true;
      default:
        return true;
    }
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = !searchQuery || 
      t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getProjectName(t.project_id).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || t.type === typeFilter;
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    const matchesDate = isWithinDateRange(t.date);
    return matchesSearch && matchesType && matchesCategory && matchesDate;
  });

  // Calculate totals for filtered transactions
  const totalExpenses = filteredTransactions
    .filter(t => t.type === 'Expense')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalCapitalIn = filteredTransactions
    .filter(t => t.type === 'Capital_In')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalRevenue = filteredTransactions
    .filter(t => t.type === 'Sale_Revenue')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalPurchasePrice = filteredTransactions
    .filter(t => t.type === 'Purchase_Price')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalReimbursements = filteredTransactions
    .filter(t => ['Reimbursement', 'Refund', 'Vendor_Reimbursement'].includes(t.type))
    .reduce((sum, t) => sum + t.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transactions-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-bold">Transactions</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          View-only transaction ledger. To edit or delete, go to the specific project.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
        <Card className="rounded-sm border" data-testid="total-purchase-summary">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Total Purchase</p>
            <p className="text-base sm:text-xl font-heading font-bold font-mono text-purple-600">
              {formatCurrency(totalPurchasePrice)}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border" data-testid="total-expenses-summary">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Total Expenses</p>
            <p className="text-base sm:text-xl font-heading font-bold font-mono text-red-600">
              {formatCurrency(totalExpenses)}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border" data-testid="total-capital-summary">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Capital In</p>
            <p className="text-base sm:text-xl font-heading font-bold font-mono text-emerald-600">
              {formatCurrency(totalCapitalIn)}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border" data-testid="total-revenue-summary">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Sale Revenue</p>
            <p className="text-base sm:text-xl font-heading font-bold font-mono text-blue-600">
              {formatCurrency(totalRevenue)}
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border" data-testid="total-reimbursements-summary">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Reimbursements</p>
            <p className="text-base sm:text-xl font-heading font-bold font-mono text-teal-600">
              {formatCurrency(totalReimbursements)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-input"
          />
        </div>
        <div className="grid grid-cols-3 sm:flex sm:flex-row gap-2 sm:gap-4">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-40 rounded-sm" data-testid="type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Purchase_Price">Purchase Price</SelectItem>
            <SelectItem value="Expense">Expense</SelectItem>
            <SelectItem value="Capital_In">Capital In</SelectItem>
            <SelectItem value="Sale_Revenue">Sale Revenue</SelectItem>
            <SelectItem value="Reimbursement">Reimbursement</SelectItem>
            <SelectItem value="Refund">Refund</SelectItem>
            <SelectItem value="EMD">EMD</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-40 rounded-sm" data-testid="category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {TRANSACTION_CATEGORY_GROUPS.map(group => (
              <React.Fragment key={`grp-${group.label}`}>
                <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/40">
                  {group.label}
                </div>
                {group.items.map(cat => (
                  <SelectItem key={cat} value={cat} className="pl-4">{cat}</SelectItem>
                ))}
              </React.Fragment>
            ))}
          </SelectContent>
        </Select>
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-full sm:w-40 rounded-sm" data-testid="date-filter">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Dates</SelectItem>
            <SelectItem value="ytd">Year to Date</SelectItem>
            <SelectItem value="last30">Last 30 Days</SelectItem>
            <SelectItem value="last90">Last 90 Days</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>
        </div>
        {dateFilter === 'custom' && (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="flex-1 sm:w-36 rounded-sm"
              placeholder="Start date"
            />
            <span className="text-muted-foreground text-sm">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="flex-1 sm:w-36 rounded-sm"
              placeholder="End date"
            />
          </div>
        )}
      </div>

      {/* Transactions Table - View Only */}
      <Card className="rounded-sm border" data-testid="transactions-table">
        <CardContent className="p-0">
          {filteredTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Project</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((t) => (
                    <tr key={t.id} data-testid={`transaction-row-${t.id}`}>
                      <td className="font-mono text-sm">{formatDate(t.date)}</td>
                      <td>
                        <Link 
                          to={`/projects/${t.project_id}`} 
                          className="text-primary hover:underline font-medium"
                        >
                          {getProjectName(t.project_id)}
                        </Link>
                      </td>
                      <td>
                        <Badge 
                          variant="outline" 
                          className={`rounded-sm text-xs ${getTypeStyles(t.type)}`}
                        >
                          {t.type.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td>{t.category}</td>
                      <td className="text-muted-foreground max-w-[200px] truncate">
                        {t.description || '-'}
                      </td>
                      <td className={`font-mono font-medium ${
                        t.type === 'Expense' ? 'text-red-600' : 
                        ['Sale_Revenue', 'Reimbursement', 'Refund'].includes(t.type) ? 'text-emerald-600' :
                        'text-foreground'
                      }`}>
                        {t.type === 'Expense' ? '-' : ['Sale_Revenue', 'Reimbursement', 'Refund'].includes(t.type) ? '+' : ''}
                        {formatCurrency(t.amount)}
                      </td>
                      <td>
                        <Link to={`/projects/${t.project_id}`}>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View in project">
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              {transactions.length === 0 ? 'No transactions recorded yet' : 'No transactions match your filters'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results count */}
      <p className="text-sm text-muted-foreground">
        Showing {filteredTransactions.length} of {transactions.length} transactions
      </p>
    </div>
  );
};

export default Transactions;
